import type { CustomerRecord, Piece } from '@/domain/types';

/**
 * Every piece a customer owns.
 *
 * `customer.pieces` is meant to be the reverse of `piece.customerId`, but almost
 * nothing fills it: `buildFixtures` back-links bookings and
 * `piece.bookingsLinked` and then stops, so exactly two seeded customers have a
 * non-empty array. Reading it alone finds nothing for records that plainly have
 * history - which is why the Continue control could never light up.
 *
 * So resolve forwards by `customerId`, and union the back-link for anything a
 * caller has appended by hand.
 */
export function piecesForCustomer(
  pieces: Record<string, Piece>,
  customer: Pick<CustomerRecord, 'id' | 'pieces'>,
): Piece[] {
  const out = Object.values(pieces).filter((p) => p.customerId === customer.id);
  for (const id of customer.pieces ?? []) {
    const p = pieces[id];
    if (p && !out.some((o) => o.id === p.id)) out.push(p);
  }
  return out;
}

/** Pieces the customer could book another sitting on. */
export function continuablePiecesFor(
  pieces: Record<string, Piece>,
  customer: Pick<CustomerRecord, 'id' | 'pieces'>,
): Piece[] {
  return piecesForCustomer(pieces, customer).filter(
    (p) => p.status === 'in-progress' || p.status === 'paused-will-return',
  );
}

/** A piece's display name, falling back through style then design type. */
export function pieceTitleOf(p: Piece): string {
  return p.title || p.style || p.designType.replace(/-/g, ' ');
}

/** "Chicano half-sleeve · session 2 of 3" - the sitting they'd be booking next. */
export function pieceSessionLabel(p: Piece): string {
  const next = (p.sessionsCompleted ?? 0) + 1;
  const of = p.estimatedTotalSessions ? ` of ${p.estimatedTotalSessions}` : '';
  return `${pieceTitleOf(p)} · session ${next}${of}`;
}
